/**
 * Senses (Foundry: PerceptionStatistic#prepareSenses and the Sense data model): the ancestry's
 * vision plus every Sense Rule Element whose predicate passes. A sense of a kind the Character
 * already has replaces it only when forced, more acute, or longer-ranged. For display, greater
 * darkvision supersedes darkvision, which supersedes low-light vision.
 */
import type { SenseData } from "./actor-data";
import { testPredicate } from "./predicate";
import type { EngineState } from "./state";
import { SENSES_WITH_MANDATORY_ACUITIES, type SenseAcuity, type SenseType } from "./values";

export interface SenseView extends SenseData {
  /** The sense's name ("Tremorsense"). */
  label: string;
  /** The full line ("Tremorsense (Imprecise) 30 Feet"); null for a sense Foundry does not list (vague scent). */
  display: string | null;
  /** Whether to emphasise the label (see invisibility, truesight). */
  emphasize: boolean;
}

const ACUITY_VALUES: Record<SenseAcuity, number> = { precise: 2, imprecise: 1, vague: 0 };

const SENSE_TYPE_LABELS: Record<string, string> = {
  bloodsense: "Bloodsense",
  darkvision: "Darkvision",
  echolocation: "Echolocation",
  "electromagnetic-sense": "Electromagnetic Sense",
  "greater-darkvision": "Greater Darkvision",
  "infrared-vision": "Infrared Vision",
  lifesense: "Lifesense",
  "low-light-vision": "Low-Light Vision",
  magicsense: "Magicsense",
  "motion-sense": "Motion Sense",
  scent: "Scent",
  "see-invisibility": "See the Unseen",
  spiritsense: "Spiritsense",
  thoughtsense: "Thoughtsense",
  tremorsense: "Tremorsense",
  truesight: "Truesight",
  wavesense: "Wavesense",
};
const ACUITY_LABELS: Record<SenseAcuity, string> = { precise: "Precise", imprecise: "Imprecise", vague: "Vague" };

/** Foundry's English formats (`PF2E.Actor.Creature.Sense.*`), overridden by the Snapshot's strings when present. */
const FORMATS = {
  WithAcuityAndRange: "{sense} ({acuity}) {range} Feet",
  WithAcuity: "{sense} ({acuity})",
  WithRange: "{sense} {range} Feet",
};

/** Merge the Sense synthetics into the actor's senses (Foundry: PerceptionStatistic#prepareSenses). */
export function prepareSenses(state: EngineState): SenseData[] {
  const prepared: SenseData[] = state.actor.system.perception.senses.map((s) => normalise(s));
  const options = state.rollOptions.get(["sense"]);
  for (const { sense, predicate, force } of state.synthetics.senses) {
    // Tested against the actor's options alone, as Foundry does (no parent: options).
    if (predicate.length > 0 && !testPredicate(predicate, options)) continue;
    const incoming = normalise(sense);
    const existing = prepared.find((s) => s.type === incoming.type);
    if (!existing) prepared.push(incoming);
    else if (force || ACUITY_VALUES[incoming.acuity] > ACUITY_VALUES[existing.acuity] || incoming.range > existing.range) {
      prepared.splice(prepared.indexOf(existing), 1, incoming);
    }
  }
  const seen = new Set<string>();
  const unique = prepared.filter((s) => (seen.has(s.type) ? false : (seen.add(s.type), true)));
  state.actor.system.perception.senses = unique;
  return unique;
}

function normalise(sense: SenseData): SenseData {
  const acuity = SENSES_WITH_MANDATORY_ACUITIES[sense.type as SenseType] ?? sense.acuity ?? "precise";
  const range = typeof sense.range === "number" && sense.range > 0 ? sense.range : Infinity;
  return { type: sense.type, acuity, range, source: sense.source ?? null };
}

/** The senses to show: each with its label, dropping vision a better vision supersedes. */
export function senseViews(state: EngineState, senses: SenseData[]): SenseView[] {
  const types = new Set(senses.map((s) => s.type));
  const superseded = (type: string): boolean =>
    (type === "low-light-vision" && (types.has("darkvision") || types.has("greater-darkvision"))) || (type === "darkvision" && types.has("greater-darkvision"));
  return senses
    .filter((s) => !superseded(s.type))
    .map((s) => ({ ...s, label: senseTypeLabel(state, s.type), display: senseDisplay(state, s), emphasize: s.type === "see-invisibility" || s.type === "truesight" }));
}

function senseTypeLabel(state: EngineState, type: string): string {
  return state.catalogue?.table("senses")?.[type] ?? SENSE_TYPE_LABELS[type] ?? type;
}

function acuityLabel(state: EngineState, acuity: SenseAcuity): string {
  return state.catalogue?.table("senseAcuities")?.[acuity] ?? ACUITY_LABELS[acuity];
}

function format(state: EngineState, key: keyof typeof FORMATS, values: Record<string, string>): string {
  const fullKey = `PF2E.Actor.Creature.Sense.${key}`;
  const template = state.catalogue?.localize(fullKey) ?? fullKey;
  return (template === fullKey ? FORMATS[key] : template).replace(/\{(\w+)\}/g, (whole, name: string) => values[name] ?? whole);
}

/** Foundry's Sense#label. */
export function senseDisplay(state: EngineState, sense: SenseData): string | null {
  const build = (acuity: SenseAcuity | null, range: number | null): string => {
    const label = senseTypeLabel(state, sense.type);
    if (acuity && range) return format(state, "WithAcuityAndRange", { sense: label, acuity: acuityLabel(state, acuity), range: String(range) });
    if (acuity) return format(state, "WithAcuity", { sense: label, acuity: acuityLabel(state, acuity) });
    if (range) return format(state, "WithRange", { sense: label, range: String(range) });
    return label;
  };
  const range = sense.range < Infinity ? sense.range : null;
  switch (sense.type) {
    case "darkvision":
    case "greater-darkvision":
    case "low-light-vision":
    case "see-invisibility":
    case "truesight":
      return build(null, null);
    case "echolocation":
    case "infrared-vision":
      return build(null, range);
    case "lifesense":
      return build(sense.acuity === "precise" ? null : sense.acuity, range);
    case "scent":
      return sense.acuity === "vague" ? null : build(sense.acuity, range);
    default:
      return build(sense.acuity, range);
  }
}
